import { useState, useCallback, useRef } from 'react'
import {
  getAvailableCharacters,
  getTaskStatus,
  updateCharacter as updateCharacterApi
} from '../../../services/api/aiVideoGeneration'
import useTaskPoller from './useTaskPoller'

/**
 * Custom hook to manage character fetching and state for VideoAi
 * Follows the same pattern as useImageGeneration and useVideoGeneration
 */
export const useCharacters = ({ onCharactersLoaded, videoId } = {}) => {
  const [characters, setCharacters] = useState([])
  const [isFetchingCharacters, setIsFetchingCharacters] = useState(false)
  const hasFetchedRef = useRef(false)

  const { startPolling: pollTask } = useTaskPoller({ pollInterval: 2000 })

  const fetchCharacters = useCallback(
    async (force = false) => {
      // Skip if already fetched and not forcing
      if (hasFetchedRef.current && !force) {
        return characters
      }

      // Skip if already fetching
      if (isFetchingCharacters) {
        return characters
      }

      try {
        setIsFetchingCharacters(true)
        const response = await getAvailableCharacters({ video_id: videoId })

        const taskId =
          response &&
          response.data &&
          (response.data['task-id'] ||
            response.data.task_id ||
            response.data.taskId)

        if (!taskId) {
          throw new Error('Missing task-id in getAvailableCharacters response')
        }

        const taskResult = await pollTask(taskId)
        const charArray = taskResult?.output || []
        if (!Array.isArray(charArray)) {
          throw new Error('Invalid response format: output is not an array')
        }

        const formattedCharacters = charArray
          .filter(Boolean)
          .map((item, index) => ({
            ...item,
            id: index + 1
          }))

        setCharacters(formattedCharacters)
        hasFetchedRef.current = true

        if (onCharactersLoaded) {
          onCharactersLoaded(formattedCharacters)
        }

        return formattedCharacters
      } catch (error) {
        console.error('Error fetching characters:', error)
        return []
      } finally {
        setIsFetchingCharacters(false)
      }
    },
    [isFetchingCharacters, onCharactersLoaded, videoId, pollTask, characters]
  )

  const editCharacter = useCallback(async (payload) => {
    try {
      const response = await updateCharacterApi(payload)
      const taskId = response?.data?.task_id || response?.task_id
      if (!taskId) throw new Error('No task ID returned')

      const result = await pollTask(taskId)
      return result
    } catch (error) {
      throw error
    }
  }, [pollTask])

  const resetCharacters = useCallback(() => {
    setCharacters([])
    hasFetchedRef.current = false
  }, [])

  return {
    characters,
    setCharacters,
    isFetchingCharacters,
    fetchCharacters,
    editCharacter,
    resetCharacters,
    hasFetched: hasFetchedRef.current
  }
}

export default useCharacters
